import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, Button, Alert, Chip, FormControlLabel, Switch, Divider, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { securityApi, type SecuritySettings } from '../../api/security.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function SecuritySettingsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('security.manage');
  const { data } = useQuery({ queryKey: ['security-settings'], queryFn: () => securityApi.settings() });
  const [form, setForm] = useState<Partial<SecuritySettings>>({});
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (data) setForm({ maxFailedLogins: data.maxFailedLogins, lockoutMinutes: data.lockoutMinutes, rateLimitEnabled: data.rateLimitEnabled, authRateLimitMax: data.authRateLimitMax, apiRateLimitMax: data.apiRateLimitMax, corsAllowedOrigins: data.corsAllowedOrigins });
  }, [data]);

  const set = <K extends keyof SecuritySettings>(k: K, v: SecuritySettings[K]) => setForm((f) => ({ ...f, [k]: v }));
  const saveMut = useMutation({
    mutationFn: () => securityApi.updateSettings(form),
    onSuccess: () => { setMsg({ kind: 'success', text: 'Security settings saved.' }); qc.invalidateQueries({ queryKey: ['security-settings'] }); },
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });

  return (
    <>
      <PageHeader title="Security Settings" subtitle="Account lockout policy, rate limits and CORS allowlist." />
      <Card sx={{ maxWidth: 760 }}>
        <CardHeader title="Policy" action={data && <Chip size="small" label={`source: ${data.source}`} color={data.source === 'database' ? 'primary' : 'default'} />} />
        <CardContent>
          <Stack spacing={2}>
            {msg && <Alert severity={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}
            <Divider textAlign="left">Account lockout</Divider>
            <Stack direction="row" spacing={2}>
              <TextField label="Max failed logins" type="number" value={form.maxFailedLogins ?? ''} onChange={(e) => set('maxFailedLogins', Number(e.target.value))} disabled={!canManage} fullWidth />
              <TextField label="Lockout minutes (0 = until admin)" type="number" value={form.lockoutMinutes ?? ''} onChange={(e) => set('lockoutMinutes', Number(e.target.value))} disabled={!canManage} fullWidth />
            </Stack>
            <Divider textAlign="left">Rate limiting</Divider>
            <FormControlLabel control={<Switch checked={form.rateLimitEnabled ?? true} onChange={(e) => set('rateLimitEnabled', e.target.checked)} disabled={!canManage} />} label="Rate limiting enabled" />
            <Stack direction="row" spacing={2}>
              <TextField label="Auth max / window" type="number" value={form.authRateLimitMax ?? ''} onChange={(e) => set('authRateLimitMax', Number(e.target.value))} disabled={!canManage} fullWidth helperText={data ? `window ${Math.round(data.authRateLimitWindowMs / 60000)} min` : ''} />
              <TextField label="API max / window" type="number" value={form.apiRateLimitMax ?? ''} onChange={(e) => set('apiRateLimitMax', Number(e.target.value))} disabled={!canManage} fullWidth helperText={data ? `window ${Math.round(data.apiRateLimitWindowMs / 1000)}s` : ''} />
            </Stack>
            <Typography variant="caption" color="text.secondary">Rate-limit windows are applied at server start (env). Max values here are recorded policy.</Typography>
            <Divider textAlign="left">CORS</Divider>
            <TextField label="Allowed origins (comma-separated)" value={form.corsAllowedOrigins ?? ''} onChange={(e) => set('corsAllowedOrigins', e.target.value)} disabled={!canManage} fullWidth helperText="Empty = allow all (dev). Applied from env at server start." />
            {canManage && <Button variant="contained" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>Save</Button>}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
